<script setup lang="ts">
import { VSF_MHJ_MY_STORE_SEARCH_TERM } from '~/config/cookie'

type SetMyStoreModalProps = {
  show: boolean
  group: string
  /** Open straight on the results for changing the current store. */
  changeStore?: boolean
  /** PDP only: products whose availability shows on each store card. */
  products?: FlattenedProduct[]
  productStoreLocation?: ProductStoreLocation
  /** Stock for the listed stores is still being fetched. */
  stockLoading?: boolean
  clearCampaignOnClose?: boolean
}
const props = defineProps<SetMyStoreModalProps>()
const emit = defineEmits<{ (e: 'close'): void }>()

const shopStore = useShopStore()
const i18n = useI18n()
const parentLocation = inject('location', '')
const datalayerGroup = parentLocation === LOCATIONS.pdp ? 'pdp find in store' : 'set my store'
const storedSearchTerm = useCookie(VSF_MHJ_MY_STORE_SEARCH_TERM)

const view = ref<'search' | 'my-store'>('search')
// A saved store id can outlive its store (closed, not loaded yet, failed clear); search is the safe fallback.
const activeView = computed(() => (view.value === 'my-store' && shopStore.myStore ? 'my-store' : 'search'))
const announcement = ref('')
const titleRef = ref<HTMLElement | null>(null)
const bodyRef = ref<HTMLElement | null>(null)
const searchRef = ref<{ search: (term: string) => Promise<void> } | null>(null)
const dialogEl = computed(() => bodyRef.value?.closest('dialog'))

const stores = computed(
  () =>
    shopStore.storesList?.filter(
      (store: SFCCStore) => store && store.c_storeStatus !== 'Closed' && store.c_clickAndCollectEnabled,
    ) || [],
)
const stock = computed(() => (props.products ? shopStore.storeStock : undefined))
// The list can come from a search that fetched no stock (e.g. the header modal), so hold
// availability back for those stores until the refresh lands instead of showing "Not available".
const stockFor = (store: SFCCStore) =>
  props.stockLoading && !stock.value?.stores?.some((line) => line.store === store.id) ? undefined : stock.value
// myStore is set before a logged-in shopper's SFCC update returns; myStoreId only after.
const selectedStoreId = computed(() => shopStore.myStore?.id ?? shopStore.myStoreId)
const myStoreName = computed(() => tidyStoreName(shopStore.myStore?.name ?? shopStore.myStoreName) ?? '')
const title = computed(() =>
  activeView.value === 'my-store' ? i18n.t('set_my_store.header_my_store') : i18n.t('set_my_store.header_set_my_store'),
)

async function showView(next: 'search' | 'my-store') {
  view.value = next
  await nextTick()
  dialogEl.value?.scrollTo({ top: 0 })
  titleRef.value?.focus()
}

async function searchFor(term: string) {
  await nextTick()
  if (term) await searchRef.value?.search(term)
}

async function showChangeStore() {
  await showView('search')
  await searchFor(myStoreName.value)
}

async function selectStore(store: SFCCStore, storeIndex: number) {
  if (store.id === selectedStoreId.value) return
  handleResultClickDataLayer({
    storeResults: stores.value,
    storeStockResults: stock.value,
    searchValue: storedSearchTerm.value?.toString() ?? '',
    storeIndex,
    linkDetails: { label: 'set my store' },
    locale: i18n.locale.value,
    group: datalayerGroup,
  })
  if (await shopStore.setMyStore(store)) {
    announcement.value = i18n.t('set_my_store.store_set', { name: tidyStoreName(store.name) })
  }
}

async function clearMyStore() {
  const clearedName = myStoreName.value
  // A failed save restores the store, so stay on its details.
  if (!(await shopStore.clearMyStore())) return
  announcement.value = i18n.t('set_my_store.store_cleared')
  await showView('search')
  await searchFor(clearedName)
}

function sendMyStoreDatalayer(linkDetails: { label: string; url?: string }) {
  if (!shopStore.myStore) return
  handleResultClickDataLayer({
    storeResults: [shopStore.myStore],
    searchValue: storedSearchTerm.value?.toString() ?? '',
    storeIndex: 0,
    linkDetails,
    locale: i18n.locale.value,
    group: datalayerGroup,
  })
}

function onOpen() {
  announcement.value = ''
  if (props.changeStore && selectedStoreId.value) {
    showChangeStore()
    return
  }
  view.value = shopStore.myStore ? 'my-store' : 'search'
  nextTick(() => dialogEl.value?.scrollTo({ top: 0 }))
}

watch(
  () => props.show,
  (isOpen) => isOpen && onOpen(),
)

onMounted(() => props.show && onOpen())
</script>

<template>
  <Modal
    class="set-my-store-modal"
    data-testid="set-my-store-modal"
    :show="show"
    :group="group"
    :clear-campaign-on-close="clearCampaignOnClose"
    border-on-scroll
    @close="emit('close')"
  >
    <template #header>
      <h2 ref="titleRef" class="set-my-store-modal__title" data-testid="set-my-store-modal__title" tabindex="-1">
        {{ title }}
      </h2>
    </template>

    <div
      ref="bodyRef"
      class="set-my-store-modal__body"
      :class="{ 'set-my-store-modal__body--my-store': activeView === 'my-store' }"
    >
      <p class="sr-only" aria-live="polite">{{ announcement }}</p>

      <template v-if="activeView === 'my-store' && shopStore.myStore">
        <StoreMyStoreCard :store="shopStore.myStore" @datalayer-click="sendMyStoreDatalayer" />
        <Button
          variant="text"
          class="set-my-store-modal__clear-store"
          data-testid="set-my-store-modal__clear-store"
          @click="clearMyStore"
        >
          {{ $t('set_my_store.clear_store') }}
        </Button>
      </template>

      <template v-else>
        <SearchStores
          ref="searchRef"
          :stores="stores"
          :products="products"
          :product-store-location="productStoreLocation"
        />
        <ul v-if="stores.length" class="set-my-store-modal__stores">
          <li v-for="(store, storeIndex) in stores" :key="store.id">
            <StoreCard
              :store="store"
              :store-index="storeIndex"
              :is-my-store="store.id === selectedStoreId"
              :stock="stockFor(store)"
              :product-store-location="productStoreLocation"
              @select="selectStore(store, storeIndex)"
              @view-details="showView('my-store')"
            />
          </li>
        </ul>
      </template>
    </div>

    <template #footer>
      <Button
        v-if="activeView === 'my-store'"
        variant="secondary"
        class="set-my-store-modal__footer-button"
        data-testid="set-my-store-modal__change-store"
        @click="showChangeStore"
      >
        {{ $t('product_details_page.find_store.button.change_store') }}
      </Button>
      <Button
        v-else
        variant="secondary"
        class="set-my-store-modal__footer-button"
        data-testid="set-my-store-modal__close"
        @click="emit('close')"
      >
        {{ $t('set_my_store.close') }}
      </Button>
    </template>
  </Modal>
</template>

<style lang="postcss">
.modal.set-my-store-modal {
  --menu-header-height: 5rem;

  .modal__header {
    align-items: flex-end;
    padding: 0 var(--space-120) var(--space-50) var(--space-50);
  }

  /* The X only fills ~53% of its 24px icon box, so a 1.9rem icon draws the design's 16px X in a 48px hit area. */
  .modal__close {
    top: 0;
    right: 0;
    width: var(--space-90);
    padding: calc((var(--space-90) - 1.9rem) / 2);

    svg.icons {
      width: 1.9rem;
      height: 1.9rem;
    }
  }

  .set-my-store-modal__title {
    margin: 0;
    font-size: var(--text-large);
    font-weight: bold;
    line-height: var(--line-height-default);
    letter-spacing: var(--letter-spacing-default);
    text-transform: uppercase;
    color: var(--text-primary);

    &:focus {
      outline: none;
    }
  }

  .modal__body {
    padding: 0 var(--space-50) var(--space-60);
  }

  .modal__footer {
    padding: var(--space-50) var(--space-50) var(--space-60);
    border-top-color: var(--border-neutral-20);
    box-shadow: 0px -4px 8px rgba(0, 0, 0, 0.05);
  }

  .set-my-store-modal__footer-button {
    width: 100%;
    max-width: none;
  }

  .set-my-store-modal__body {
    position: relative; /* required for the SearchStores spinner */
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
  }

  .set-my-store-modal__body--my-store {
    padding-top: var(--space-30);
  }

  .store-search {
    gap: var(--space-30);

    .store-search__cta {
      color: var(--text-secondary);
      letter-spacing: var(--letter-spacing-default);
    }

    .store-search__results-heading {
      padding-top: var(--space-30);
    }
  }

  .set-my-store-modal__stores {
    display: flex;
    flex-direction: column;
    gap: var(--space-30);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .set-my-store-modal__clear-store {
    align-self: center;
    font-size: var(--text-small);
    letter-spacing: var(--letter-spacing-default);
  }
}
</style>
